import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useState,
} from "react";
import { Children, Tenant, FieldMetadata, Location } from "types";
import { DELIMITER, getNext, getPrevious } from "utils";
import { t } from "i18next";
import getPermitFieldMetadata from "pages/permits/migrationWizard/permitFieldMetadata";
import { WizardWorkerOutgoingMessage } from "./wizardWorker";
import { AppContext } from "context/appContext";
import {
  ImportWizardState,
  ImportWizardAction,
  WIZARD_ACTION,
  ImportStep,
  GetNextUnconfirmedFieldArgs,
  CreateEntityParams,
  IdentifyDuplicatesParams,
} from "types/ImportWizard";
import { VehicleParser } from "components/importer/VehicleParser";

const defaultState = <T extends object, F extends string>(): ImportWizardState<
  T,
  F
> => ({
  entityName: "",
  activeStep: ImportStep.UPLOAD,
  csvData: [],
  csvHeaders: [],
  modifiedData: [],
  columnMappings: {},
  fileName: "",
  confirmedFields: [],
  entityFields: [],
  previewEntities: [],
  stringifiedEntities: "",
  failedImports: [],
  discardedRows: [],
  failedImport: null,
  tenant: null,
  hasValidCsvData: false,
  hasValidColumnMappings: false,
  csvUsesTemplate: false,
  focusedRows: [],
  selectedSystemField: null,
  selectedCsvField: null,
  fieldMetadata: null,
  savedImportMappings: [],
  defaultAddress: {
    street: "",
    city: "",
    stateCode: "",
    postalCode: "",
  },
});

export function getInitialState<T extends object, F extends string>(
  tenant: Tenant | null,
  fieldMetadata: Record<F, FieldMetadata<F>>,
  entityName: string,
  defaultAddress: Location,
): ImportWizardState<T, F> {
  return {
    ...defaultState<T, F>(),
    tenant,
    entityName,
    fieldMetadata,
    entityFields: Object.keys(fieldMetadata) as F[],
    savedImportMappings: tenant?.data.savedImportMappings || [],
    defaultAddress,
  };
}
export function getRowsWithInvalidValues(
  data: Record<string, string>[],
  csvField: string,
  validator: (value: string | undefined) => boolean,
): number[] {
  const rowIndicesWithInvalidValues: number[] = [];
  data.forEach((row, index) => {
    const value = row[csvField];
    if (!validator(value)) {
      rowIndicesWithInvalidValues.push(index);
    }
  });
  return rowIndicesWithInvalidValues;
}
export function repairCsvData(
  csvData: Record<string, string>[],
  csvHeaders: string[],
): Record<string, string>[] {
  if (csvData.length === 0 || csvHeaders.length === 0) return csvData;

  return csvData.map((row: Record<string, string>) => {
    const repairedRow: Record<string, string> = {};
    // this is how Papa.parse marks extra fields, the cast is necessary for TS
    const extraFields: string[] =
      (row["__parsed_extra"] as unknown as string[]) || [];

    csvHeaders.forEach((header) => {
      if (header in row) {
        repairedRow[header] = row[header];
      } else if (extraFields.length > 0) {
        // If we're missing a field but have extra fields, use one of those
        repairedRow[header] = extraFields.shift() || "";
      } else {
        // If we're missing a field and have no extras, pad with an empty string
        repairedRow[header] = "";
      }
    });

    // If we have any remaining extra fields, append them to the last header
    if (extraFields.length > 0) {
      const lastHeader = csvHeaders[csvHeaders.length - 1];
      repairedRow[lastHeader] += ", " + extraFields.join(", ");
    }

    return repairedRow;
  });
}
export function getCombinedFieldKey(
  fields: { csvField: string; trailingCharacter: string }[],
): string {
  let key = "_combined_";
  fields.forEach((field) => {
    key += `${field.csvField}${DELIMITER}${field.trailingCharacter}${DELIMITER}`;
  });
  // remove the trailing delimiter
  key = key.slice(0, -1);
  return key;
}
export function getValueForCombinedField(
  row: Record<string, string>,
  combinedFieldKey: string,
): string {
  if (!combinedFieldKey.startsWith("_combined_")) {
    return "";
  }
  // remove the leading '_combined_'
  const strippedKey = combinedFieldKey.slice(10);
  const strArr = strippedKey.split(DELIMITER);
  const fields: { csvField: string; trailingCharacter: string }[] = [];

  strArr.forEach((field, index) => {
    if (index % 2 === 0) {
      fields.push({
        csvField: row[field],
        trailingCharacter: strArr[index + 1] || "",
      });
    }
  });

  return fields
    .map(({ csvField, trailingCharacter }) => `${csvField}${trailingCharacter}`)
    .join("");
}
export function getNextUnconfirmedField<F extends string>({
  fromField,
  allFields,
  confirmedFields,
}: GetNextUnconfirmedFieldArgs<F>): F | null {
  // find the next unconfirmed field in order
  let nextUnconfirmedField: F | null = null;
  const currentIndex = allFields.findIndex((f) => f === fromField);
  let orderedFields: F[];

  if (currentIndex === -1) {
    orderedFields = allFields;
  } else {
    orderedFields = [
      ...allFields.slice(currentIndex),
      ...allFields.slice(0, currentIndex),
    ];
  }

  for (const field of orderedFields) {
    if (!confirmedFields.includes(field)) {
      nextUnconfirmedField = field;
      break;
    }
  }

  return nextUnconfirmedField;
}

function reducer<T extends object, F extends string>(
  state: ImportWizardState<T, F>,
  action: ImportWizardAction<T, F>,
) {
  let newState: ImportWizardState<T, F>;
  switch (action.type) {
    case WIZARD_ACTION.HANDLE_NEXT:
      newState = {
        ...state,
        activeStep: getNext(
          state.activeStep,
          Object.values(ImportStep),
          ImportStep.UPLOAD,
        ),
      };
      break;
    case WIZARD_ACTION.HANDLE_BACK:
      newState = {
        ...state,
        activeStep: getPrevious(
          state.activeStep,
          Object.values(ImportStep),
          ImportStep.UPLOAD,
        ),
      };
      break;
    case WIZARD_ACTION.SET_SELECTED_SYSTEM_FIELD:
      newState = {
        ...state,
        selectedSystemField: action.payload,
      };
      break;
    case WIZARD_ACTION.SELECT_NEXT_SYSTEM_FIELD:
      newState = {
        ...state,
        selectedSystemField: getNext(
          state.selectedSystemField,
          state.entityFields,
          null,
        ),
      };
      break;
    case WIZARD_ACTION.SELECT_NEXT_CSV_FIELD:
      newState = {
        ...state,
        selectedCsvField: getNext(
          state.selectedCsvField,
          state.csvHeaders,
          null,
        ),
      };
      break;
    case WIZARD_ACTION.SELECT_PREVIOUS_SYSTEM_FIELD:
      newState = {
        ...state,
        selectedSystemField: getPrevious(
          state.selectedSystemField,
          state.entityFields,
          null,
        ),
      };
      break;
    case WIZARD_ACTION.SELECT_PREVIOUS_CSV_FIELD:
      newState = {
        ...state,
        selectedCsvField: getPrevious(
          state.selectedCsvField,
          state.csvHeaders,
          null,
        ),
      };
      break;
    case WIZARD_ACTION.SET_SELECTED_CSV_FIELD:
      newState = {
        ...state,
        selectedCsvField: action.payload,
      };
      break;
    case WIZARD_ACTION.SET_COLUMN_MAPPINGS:
      newState = {
        ...state,
        columnMappings: action.payload,
      };
      break;
    case WIZARD_ACTION.SET_CSV_HEADERS:
      newState = {
        ...state,
        csvHeaders: action.payload,
      };
      break;
    case WIZARD_ACTION.SET_HAS_VALID_CSV_DATA:
      newState = {
        ...state,
        hasValidCsvData: action.payload,
      };
      break;
    case WIZARD_ACTION.SET_HAS_VALID_COLUMN_MAPPINGS:
      newState = {
        ...state,
        hasValidColumnMappings: action.payload,
      };
      break;
    case WIZARD_ACTION.SET_FILE_NAME:
      newState = {
        ...state,
        fileName: action.payload,
      };
      break;
    case WIZARD_ACTION.SET_MODIFIED_DATA:
      newState = {
        ...state,
        modifiedData: action.payload,
      };
      break;
    case WIZARD_ACTION.CONFIRM_MAPPING: {
      const updatedConfirmedFields = [...state.confirmedFields, action.payload];
      newState = {
        ...state,
        confirmedFields: updatedConfirmedFields,
        selectedSystemField: getNextUnconfirmedField({
          fromField: state.selectedSystemField,
          allFields: state.entityFields,
          confirmedFields: updatedConfirmedFields,
        }),
      };
      break;
    }
    case WIZARD_ACTION.SET_FOCUSED_ROWS:
      newState = {
        ...state,
        focusedRows: action.payload,
      };
      break;
    case WIZARD_ACTION.SET_FAILED_IMPORTS: {
      if (action.payload.length > 0) {
        const firstFailedImport = action.payload[0].entity;
        newState = {
          ...state,
          failedImports: action.payload,
          failedImport: firstFailedImport,
        };
      } else {
        newState = {
          ...state,
          failedImports: [],
          failedImport: null,
        };
      }
      break;
    }
    case WIZARD_ACTION.POP_FAILED_IMPORT:
      if (state.failedImports.length > 1) {
        // queue up the next failed import
        newState = {
          ...state,
          failedImports: state.failedImports.slice(1),
          failedImport: state.failedImports[1].entity,
        };
      } else {
        // all done, clear the failed imports
        newState = {
          ...state,
          failedImports: [],
          failedImport: null,
          activeStep: ImportStep.COMPLETE,
        };
      }
      break;
    case WIZARD_ACTION.SET_DISCARDED_ROWS:
      newState = {
        ...state,
        discardedRows: action.payload,
      };
      break;
    case WIZARD_ACTION.RESET_STATE:
      newState = {
        ...getInitialState(
          state.tenant,
          state.fieldMetadata as Record<F, FieldMetadata<F>>,
          state.entityName,
          state.defaultAddress,
        ),
      };
      break;
    case WIZARD_ACTION.UPDATE_FAILED_IMPORT:
      newState = {
        ...state,
        failedImport: action.payload,
      };
      break;
    case WIZARD_ACTION.SET_CSV_USES_TEMPLATE:
      newState = {
        ...state,
        csvUsesTemplate: action.payload,
      };
      break;
    case WIZARD_ACTION.GENERATE_DATA:
      newState = {
        ...state,
        csvHeaders: [...state.csvHeaders, `_generated_${action.payload.field}`],
        modifiedData: state.modifiedData.map((row) => {
          return {
            ...row,
            [`_generated_${action.payload.field}`]: action.payload.value,
          };
        }),
        columnMappings: {
          ...state.columnMappings,
          [action.payload.field]: `_generated_${action.payload.field}`,
          [`_generated_${action.payload.field}`]: action.payload.value,
        },
      };
      break;
    case WIZARD_ACTION.APPEND_COLUMN_MAPPING:
      newState = {
        ...state,
        columnMappings: {
          ...state.columnMappings,
          [action.payload.systemField]: action.payload.csvField,
        },
      };
      break;
    case WIZARD_ACTION.CLEAR_FIELD_MAPPING: {
      const { systemField, csvField } = action.payload;
      const newColumnMappings = { ...state.columnMappings };
      delete newColumnMappings[systemField];
      const newConfirmedFields = state.confirmedFields.filter(
        (f) => f !== systemField,
      );
      let newCsvHeaders = state.csvHeaders;
      let newModifiedData = state.modifiedData;

      if (csvField.charAt(0) === "_") {
        // Remove the virtual column from headers and modified data
        newCsvHeaders = state.csvHeaders.filter(
          (header) => header !== csvField,
        );
        newModifiedData = state.modifiedData.map((row) => {
          const newRow = { ...row };
          delete newRow[csvField];
          return newRow;
        });
      }

      newState = {
        ...state,
        columnMappings: newColumnMappings,
        csvHeaders: newCsvHeaders,
        modifiedData: newModifiedData,
        confirmedFields: newConfirmedFields,
      };
      break;
    }
    case WIZARD_ACTION.AUTOFILL_ADDRESS: {
      const updatedConfirmedFields: F[] = [
        ...state.confirmedFields,
        `${state.entityName}.contact.location.street` as F,
        `${state.entityName}.contact.location.city` as F,
        `${state.entityName}.contact.location.stateCode` as F,
        `${state.entityName}.contact.location.postalCode` as F,
      ];
      const nextUnconfirmedField = getNextUnconfirmedField({
        fromField: state.selectedSystemField,
        allFields: state.entityFields,
        confirmedFields: updatedConfirmedFields,
      });

      newState = {
        ...state,
        columnMappings: {
          ...state.columnMappings,
          [`${state.entityName}.contact.location.street`]:
            "_defaultAddress.street",
          [`${state.entityName}.contact.location.city`]: "_defaultAddress.city",
          [`${state.entityName}.contact.location.stateCode`]:
            "_defaultAddress.stateCode",
          [`${state.entityName}.contact.location.postalCode`]:
            "_defaultAddress.postalCode",
          _autofill_address: "true",
        },
        csvHeaders: [
          ...state.csvHeaders,
          "_defaultAddress.street",
          "_defaultAddress.city",
          "_defaultAddress.stateCode",
          "_defaultAddress.postalCode",
        ],
        modifiedData: state.modifiedData.map((row) => {
          return {
            ...row,
            "_defaultAddress.street": state.defaultAddress.street || "",
            "_defaultAddress.city": state.defaultAddress.city || "",
            "_defaultAddress.stateCode": state.defaultAddress.stateCode || "",
            "_defaultAddress.postalCode": state.defaultAddress.postalCode || "",
          };
        }),
        confirmedFields: updatedConfirmedFields,
        selectedSystemField: nextUnconfirmedField,
      };
      break;
    }
    case WIZARD_ACTION.SET_STRINGIFIED_IMPORTS:
      newState = {
        ...state,
        stringifiedEntities: action.payload,
      };
      break;
    case WIZARD_ACTION.VALIDATE_ALL_MAPPED_FIELDS: {
      const validFields: F[] = [...state.confirmedFields];

      const mappedUnconfirmedFields = Object.keys(state.columnMappings).filter(
        (field) => !state.confirmedFields.includes(field as F),
      );

      for (const field of mappedUnconfirmedFields) {
        const csvField = state.columnMappings[field];
        if (field.charAt(0) !== "_" && state.fieldMetadata) {
          // ignore virtual fields
          const invalidRowIndices = getRowsWithInvalidValues(
            state.modifiedData,
            csvField,
            state.fieldMetadata[field as F].validator,
          );
          if (invalidRowIndices.length === 0) {
            validFields.push(field as F);
          }
        }
      }

      // find the next unconfirmed field in order
      const nextUnconfirmedField = getNextUnconfirmedField({
        fromField: state.selectedSystemField,
        allFields: state.entityFields,
        confirmedFields: validFields,
      });

      newState = {
        ...state,
        confirmedFields: validFields,
        selectedSystemField: nextUnconfirmedField,
      };
      break;
    }
    case WIZARD_ACTION.IMPORT_CSV_DATA: {
      const {
        fileName,
        results: { data, errors, meta },
      } = action.payload;
      const templateHeaders = state.entityFields.map(
        (header: F) => t(header as string, { ns: "entities" }) as string,
      );
      let rows = data.map((row: Record<string, string>) => {
        return Object.fromEntries(
          Object.entries(row).map(([key, value]) => [
            key.trim() || "",
            value.trim() || "",
          ]),
        );
      });

      let csvHeaders = meta?.fields?.map((field: string) => field.trim()) || [];
      let newColumnMappings: Record<string, string> = {};

      // if we don't have any mappings, we will attempt to automatically map some columns
      if (Object.keys(state.columnMappings).length === 0) {
        newColumnMappings = csvHeaders.reduce(
          (acc, header) => {
            const foundIndex = templateHeaders.findIndex(
              (templateHeader) =>
                templateHeader.toLowerCase() === header.toLowerCase(),
            );
            if (foundIndex >= 0) {
              acc[state.entityFields[foundIndex]] = header;
            }
            return acc;
          },
          {} as Record<string, string>,
        );
      } else {
        // use imported mappings if they exist
        csvHeaders = state.csvHeaders;
        newColumnMappings = state.columnMappings;
      }

      let csvUsesTemplate = false;
      if (
        state.entityFields.every(
          (field, index) => field === Object.keys(newColumnMappings)[index],
        )
      ) {
        const firstCell = data[0][csvHeaders[0]];
        if (firstCell.startsWith("** DO NOT EDIT THIS ROW **")) {
          csvUsesTemplate = true;
          rows = rows.slice(1);
        }
      }

      const errorRowIndecies: number[] = errors
        .filter((error) => typeof error?.row === "number")
        .map((error) => error.row as number);
      // if there are errors, we need to repair the csv data
      // to make sure every row has the same number of columns
      if (errors.length > 0) {
        rows = repairCsvData(rows, csvHeaders);
      }

      newState = {
        ...state,
        fileName,
        csvHeaders,
        csvData: rows,
        modifiedData: rows,
        csvUsesTemplate: csvUsesTemplate,
        focusedRows: errorRowIndecies,
        columnMappings: newColumnMappings,
        hasValidCsvData: true,
      };
      break;
    }
    case WIZARD_ACTION.MAP_COMBINED_FIELDS: {
      const { systemField, fields } = action.payload;

      const combinedFieldKey = getCombinedFieldKey(fields);
      const updatedCsvHeaders = [...state.csvHeaders, combinedFieldKey];
      const updatedColumnMappings = {
        ...state.columnMappings,
        [systemField]: combinedFieldKey,
      };

      const updatedModifiedData = state.modifiedData.map((row) => {
        return {
          ...row,
          [combinedFieldKey]: getValueForCombinedField(row, combinedFieldKey),
        };
      });
      newState = {
        ...state,
        csvHeaders: updatedCsvHeaders,
        columnMappings: updatedColumnMappings,
        modifiedData: updatedModifiedData,
      };
      break;
    }
    case WIZARD_ACTION.IMPORT_SAVED_MAPPINGS: {
      const mappings = action.payload;
      const updatedCsvHeaders = [...state.csvHeaders];
      let updatedData = [...state.modifiedData];

      Object.entries(mappings).forEach(([key, value]) => {
        if (key.startsWith("_generated_")) {
          const csvField = key;
          updatedCsvHeaders.push(csvField); // add the generated field to the csv headers
          updatedData = updatedData.map((row) => {
            return {
              ...row,
              [csvField]: value,
            };
          });
          return;
        }
        if (
          key.startsWith("_autofill_address") &&
          !updatedCsvHeaders.includes("_defaultAddress.street")
        ) {
          updatedCsvHeaders.push(
            "_defaultAddress.street",
            "_defaultAddress.city",
            "_defaultAddress.stateCode",
            "_defaultAddress.postalCode",
          );
          updatedData = updatedData.map((row) => {
            return {
              ...row,
              "_property.contact.location.street":
                state.defaultAddress.street || "",
              "_property.contact.location.city":
                state.defaultAddress.city || "",
              "_property.contact.location.stateCode":
                state.defaultAddress.stateCode || "",
              "_property.contact.location.postalCode":
                state.defaultAddress.postalCode || "",
            };
          });
          return;
        }
        if (key.startsWith("_mapped_vehicle_")) {
          const vehicleParser = new VehicleParser();
          const csvField = value;
          updatedCsvHeaders.push(
            `_vehicle.year`,
            `_vehicle.color`,
            `_vehicle.make`,
            `_vehicle.model`,
          );
          updatedData = updatedData.map((row) => {
            const parsedVehicleData = vehicleParser.parse(row[csvField]);
            return {
              ...row,
              "_vehicle.year": parsedVehicleData.year,
              "_vehicle.color": parsedVehicleData.color,
              "_vehicle.make": parsedVehicleData.make,
              "_vehicle.model": parsedVehicleData.model,
            };
          });
        }
      });

      newState = {
        ...state,
        columnMappings: mappings,
        csvHeaders: updatedCsvHeaders,
        modifiedData: updatedData,
      };
      break;
    }
    case WIZARD_ACTION.SET_PARSED_VEHICLE_DATA:
      newState = {
        ...state,
        csvHeaders: [
          ...state.csvHeaders.filter(
            (header) => !header.startsWith("_vehicle"),
          ),
          `_vehicle.year`,
          `_vehicle.color`,
          `_vehicle.make`,
          `_vehicle.model`,
        ],
        modifiedData: state.modifiedData.map((row, index) => ({
          ...row,
          [`_vehicle.color`]: action.payload.parsedVehicleData[index].color,
          [`_vehicle.make`]: action.payload.parsedVehicleData[index].make,
          [`_vehicle.model`]: action.payload.parsedVehicleData[index].model,
          [`_vehicle.year`]: action.payload.parsedVehicleData[index].year,
        })),
        columnMappings: {
          ...state.columnMappings,
          [`${state.entityName}.vehicle.color`]: `_vehicle.color`,
          [`${state.entityName}.vehicle.make`]: `_vehicle.make`,
          [`${state.entityName}.vehicle.model`]: `_vehicle.model`,
          [`${state.entityName}.vehicle.year`]: `_vehicle.year`,
          [`_mapped_vehicle_${action.payload.csvHeader}`]:
            action.payload.csvHeader,
        },
      };
      break;
    case WIZARD_ACTION.SET_PREVIEW_IMPORTS:
      newState = {
        ...state,
        previewEntities: action.payload,
      };
      break;
    case WIZARD_ACTION.SET_ENTITY_NAME:
      newState = {
        ...state,
        entityName: action.payload,
      };
      break;
    case WIZARD_ACTION.SET_TENANT:
      newState = {
        ...state,
        tenant: action.payload,
      };
      break;
    default:
      newState = state;
      break;
  }

  // sessionStorage.setItem('importWizardState', JSON.stringify(newState));

  return newState;
}

export const ImportWizardContext = createContext<{
  state: ImportWizardState<any, any>;
  dispatch: React.Dispatch<any>;
  rowsWithInvalidValues: number[];
  processEntities: (
    data: Record<string, string>[],
    mappings: Record<string, string>,
  ) => void;
  fieldMetadata: Record<string, FieldMetadata<string>>;
  entityName: string;
  parseVehicleData: (csvHeader: string) => void;
  defaultAddress: Location;
}>({
  state: getInitialState(null, getPermitFieldMetadata([]), "", {}),
  dispatch: () => {
    // do nothing - this is only for the purpose of the initial value
  },
  rowsWithInvalidValues: [] as number[],
  processEntities: () => {
    // do nothing - this is only for the purpose of the initial value
  },
  fieldMetadata: {},
  entityName: "",
  parseVehicleData: () => {
    // do nothing - this is only for the purpose of the initial value
  },
  defaultAddress: {
    street: "",
    city: "",
    stateCode: "",
    postalCode: "",
  },
});

/**
 * Generic import wizard context provider
 * @template T The type of entity being imported
 * @template F The type of field names for the entity
 */
interface ImportWizardContextProviderProps<T extends object, F extends string>
  extends Children {
  metadata: Record<F, FieldMetadata<F>>;
  createEntity: (params: CreateEntityParams) => T;
  identifyDuplicates: (params: IdentifyDuplicatesParams) => number[];
  entityName: string;
  defaultAddress: Location;
}

const ImportWizardContextProvider = <T extends object, F extends string>({
  children,
  metadata,
  createEntity,
  identifyDuplicates,
  entityName,
  defaultAddress,
}: ImportWizardContextProviderProps<T, F>): ReactNode => {
  const { state: appState } = useContext(AppContext);
  const [state, dispatch] = useReducer(
    reducer,
    getInitialState(
      appState.tenant,
      metadata,
      entityName.toLowerCase(),
      defaultAddress,
    ),
  );
  const [entityWorker, setEntityWorker] = useState<Worker | null>(null);
  const [rowsWithInvalidValues, setRowsWithInvalidValues] = useState<number[]>(
    [],
  );

  useEffect(() => {
    if (state.selectedSystemField && state.fieldMetadata) {
      const { validator } = state.fieldMetadata[state.selectedSystemField];
      const csvField = state.columnMappings[state.selectedSystemField];
      if (csvField) {
        const invalidRows = getRowsWithInvalidValues(
          state.modifiedData,
          csvField,
          validator,
        );
        setRowsWithInvalidValues(invalidRows);
      } else {
        setRowsWithInvalidValues([]);
      }
    } else {
      setRowsWithInvalidValues([]);
    }
  }, [
    state.modifiedData,
    state.selectedSystemField,
    state.columnMappings,
    state.fieldMetadata,
  ]);

  useEffect(() => {
    const newWorker = new Worker(new URL("./wizardWorker.ts", import.meta.url));
    newWorker.onmessage = (e: MessageEvent<WizardWorkerOutgoingMessage>) => {
      const { type, payload } = e.data as WizardWorkerOutgoingMessage;
      if (type === "ARRAY_RESULT") {
        dispatch({
          type: WIZARD_ACTION.SET_STRINGIFIED_IMPORTS,
          payload,
        });
      }
    };
    setEntityWorker(newWorker);
    return () => {
      newWorker.terminate();
    };
  }, []);

  const processEntities = useCallback(
    (data: Record<string, string>[], mappings: Record<string, string>) => {
      if (!entityWorker) return;
      const previewEntities: T[] = [];

      const duplicates = identifyDuplicates({
        columnMappings: mappings,
        data,
      });
      dispatch({
        type: WIZARD_ACTION.SET_DISCARDED_ROWS,
        payload: duplicates,
      });

      data.forEach((row, index) => {
        if (duplicates.includes(index)) {
          return;
        }

        const entity = createEntity({
          row,
          mappings,
        });

        if (previewEntities.length < 100) {
          previewEntities.push(entity);
        }
        entityWorker.postMessage({
          type: "APPEND",
          payload: JSON.stringify(entity),
        });
      });

      dispatch({
        type: WIZARD_ACTION.SET_PREVIEW_IMPORTS,
        payload: previewEntities,
      });
      entityWorker.postMessage({ type: "GET_RESULT" });
    },
    [createEntity, identifyDuplicates, entityWorker],
  );

  const parseVehicleData = useCallback(
    (csvHeader: string) => {
      if (!csvHeader) {
        return;
      }
      const vehicleParser = new VehicleParser();
      // todo: offload this to a worker
      const parsedVehicleData: {
        color: string;
        make: string;
        model: string;
        year: string;
      }[] = [];

      for (let i = 0; i < state.modifiedData.length; i++) {
        const row = state.modifiedData[i];
        parsedVehicleData.push(vehicleParser.parse(row[csvHeader]));
      }

      dispatch({
        type: WIZARD_ACTION.SET_PARSED_VEHICLE_DATA,
        payload: {
          csvHeader: csvHeader,
          parsedVehicleData: parsedVehicleData,
        },
      });
    },
    [state.modifiedData, dispatch],
  );

  return (
    <ImportWizardContext.Provider
      value={{
        state,
        dispatch,
        rowsWithInvalidValues,
        processEntities,
        fieldMetadata: metadata as Record<F, FieldMetadata<F>>,
        entityName: entityName,
        parseVehicleData,
        defaultAddress,
      }}
    >
      {children}
    </ImportWizardContext.Provider>
  );
};

export default ImportWizardContextProvider;
